// CSS
import './DocumentPreviewModal.css';
// React hooks
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// Skybase hooks
import { useData } from '../../../../context/DataContext';
import { API_BASE_URL } from '../../../../config';
// External Hooks/Components
import { useAuth as useClerkAuth } from '@clerk/react';
import { useMediaQuery } from 'react-responsive';
// Media Assets
import bookmarkIcon from '../../../../assets/StandardIcons/BookmarkIcons/bookmark-icon-black-outline-transparent.png';  // Bookmark icon
import bookmarkIcon_filled from '../../../../assets/OverlayIcons/bookmark-icon-filled.png';                             // Bookmark icon (filled)
import closeIcon from '../../../../assets/StandardIcons/close-icon-black.svg';                                          // Close icon
import triangleArrow from '../../../../assets/StandardIcons/ArrowIcons/triangle-arrow.svg';                             // Triange arrow icon
import ChevronIcon from '../../../../assets/StandardIcons/ArrowIcons/chevron.png';                                      // Chevron icon



// ====================
// DocumentPreviewModal
// ====================
function DocumentPreviewModal({
    uuid,                           // String   – the UUID of this Document
    isReadOnly,                     // bool     – is semantic chunk nav active?
    isNavigationEnabled,            // bool     – can the User "swipe" between various consecutive documents?
    leftNavButtonClickCallback,     // ()       – the function to activate upon leftNavButton onClick.
    rightNavButtonClickCallback,    // ()       – the function to activate upon rightNavButton onClick.
    triggerClose,                   // ()       – the function to activate upon closing of this modal.
}){

    // destructure global context
    const { getToken } = useClerkAuth();
    const {
        downloads,
        addToDownloadsBox,
        deleteFromDownloadsBox,
        activeEngineBookmarkJson,
        createBookmark,
        removeBookmark,
    } = useData();
    
    // init hooks
    const navigate = useNavigate('');
    const documentPreviewModalRef = useRef(null);

    // define state vars
    const [documentJson, setDocumentJson] = useState(null);
    const [sourceFolderJson, setSourceFolderJson] = useState(null);
    const [sourceEngineJson, setSourceEngineJson] = useState(null);
    const [sourceOrganizationJson, setSourceOrganizationJson] = useState(null);

    // responsive CSS
    const isMobile = useMediaQuery({ maxWidth: 650 });

    
    // ============
    // fetch() JSON
    // ============
    useEffect(() => {
        // fetch JSON
        async function fetchJson() {
            try {

                // Clerk JWT Authe.
                const token = await getToken();
                if (!token) { return }; // prevent fetch() if Clerk JWT DNE.
                const configs = {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                };

                // `GET /documents/{id}`
                const response_object = await fetch(`${API_BASE_URL}/api/v1/documents/${encodeURIComponent(uuid)}`, configs);
                if (!response_object.ok) { throw new Error(`SKYBASE (client): HTTP error ${response_object.status} encountered when fetching document ${uuid}`); }
                const doc_json = await response_object.json();

                // `GET /folders/{id}`
                const folder_response = await fetch(`${API_BASE_URL}/api/v1/folders/${encodeURIComponent(doc_json.source_folder_id)}`, configs);
                if (!folder_response.ok) { throw new Error(`SKYBASE (client): HTTP error (${folder_response.status}) - Fetching source_folder (ID: ${doc_json.source_folder_id})`)};
                const source_folder_json = await folder_response.json();

                // `GET /engines/{id}`
                const source_engine_response = await fetch(`${API_BASE_URL}/api/v1/engines/${encodeURIComponent(source_folder_json.source_engine)}`); // public route
                if (!source_engine_response.ok) { throw new Error(`SKYBASE (client): HTTP error (${source_engine_response.status}) - Fetching Engine (ID: ${source_folder_json.source_engine})`)};
                const source_engine_json = await source_engine_response.json();

                // `GET /organizations/{id}`
                const source_organization_response = await fetch(`${API_BASE_URL}/api/v1/organizations/${encodeURIComponent(source_engine_json.source_organization)}`); // public route
                if (!source_organization_response.ok) { throw new Error(`SKYBASE (client): HTTP error (${source_organization_response.status}) - Fetching Organization (ID: ${source_engine_json.source_organization})`)};
                const source_organization_json = await source_organization_response.json();

                // Trigger re-renders
                setDocumentJson(doc_json); // triggers re-render
                setSourceFolderJson(source_folder_json); // triggers re-render
                setSourceEngineJson(source_engine_json); // triggers re-render
                setSourceOrganizationJson(source_organization_json); // triggers re-render

            } catch (err) {
                console.error(err);
            }
        }

        // activate json fetch
        fetchJson();
    }, [uuid])
 

    // ======================
    // Handle Bookmark status
    // ======================
    // onMount – check isBookmarked?
    const [isBookmarked, setIsBookmarked] = useState(activeEngineBookmarkJson?.some((bookmark_json) => bookmark_json.id === uuid));
    useEffect(() => {
        setIsBookmarked(activeEngineBookmarkJson?.some((bookmark_json) => bookmark_json.item_id === uuid));
    }, [activeEngineBookmarkJson]);
    // handleBookmarkButtonClick() – add/remove this Document to/from Bookmarks.
    const handleBookmarkButtonClick = () => {

        // A) get the corresp. json if it exists
        // B) ..or establish UNDEFINED if DNE
        const bookmark_json = activeEngineBookmarkJson?.find((bookmark_json) => bookmark_json.item_id === uuid);

        if (bookmark_json) {
            // delete the Bookmark if it exists
            removeBookmark(bookmark_json.id);
        } else {
            // ..otherwise, create a new Bookmark if it DNE
            createBookmark(uuid);
        }
    }


    // ================================
    // Handle Semantic Chunk Navigation – Semantic chunks are pieces of Document text that are HIGHLY-relevant to the User's search-query.
    // ⚠️ TO-DO: Impl. this feature.
    // ================================
    const [semanticChunkCounter, setSemanticChunkCounter] = useState(0);    // always start at the first semantic chunk
    const semanticChunkMaximum = 5;                                         // ⚠️ TO-DO: Remove this hard-code (retrieve this info. from backend)
    // Handle chunk increment/decrement
    const handleSemanticChunkIncrement = () => { 
        // increment only if the chunk counter does not overflow.
        if (semanticChunkCounter + 1 <= semanticChunkMaximum) {
            setSemanticChunkCounter((prev) => prev + 1)
        } else { return }
    }
    const handleSemanticChunkDecrement = () => {
        // decrement only if the chunk counter does not reach zero.
        if (semanticChunkCounter - 1 > 0) { 
            setSemanticChunkCounter((prev) => prev - 1)
         } else { return }
    }
    // Reset the semantic chunk counter when user switches between Documents (a.k.a the UUID in DocumentPreviewModal changes)
    useEffect( () => {
        setSemanticChunkCounter(0); // reset to default
    }, [uuid])


    // =====================
    // Handle KeyDown Events - respond to certain keyboard inputs
    // =====================
    // onMount
    useEffect( () => {
        // auto-focus this component
        documentPreviewModalRef.current.focus();
    }, [documentJson]);
    
    // respond to keyboard input
    const handleKeyDown = (event) => {
        // prevent default HTML behavior
        event.preventDefault();

        // Up/Down (semantic chunk increment/decrement)
        if (event.key === "ArrowUp") {
            handleSemanticChunkDecrement();
        } else if (event.key === "ArrowDown") {
            handleSemanticChunkIncrement();
        }

        // D (download button toggle)
        // if (event.key === "d" || event.key === "D") { handleDownloadButtonClick() }

        // B (bookmark button toggle)
        if (event.key === "b" || event.key === "B") { handleBookmarkButtonClick() }

        // close modal if escape-key hit
        if (event.key === "Escape") { triggerClose() };
    };

    
    // =============
    // JSX Component (Animated Placeholder) – displays while awaiting fetch() requests
    // =============
    if (!documentJson) {
        return(
            <div className='DocumentPreviewModal' ref={documentPreviewModalRef}></div>
        )
    }


    // =============
    // JSX Component
    // =============
    return(
        <div className='DocumentPreviewModal'
            ref={documentPreviewModalRef}
            tabIndex={0}                    // make the div focusable
            onKeyDown={handleKeyDown}       // listens only when focused
            >
            
            {/* Navigation overlay */}
            { (isNavigationEnabled && !isMobile) && 
                <div className='DocumentPreviewModal-nav-overlay'>
                    
                    {/* "Navigate-left" button */}
                    <div className='DocumentPreviewModal-nav-overlay-button' onClick={() => leftNavButtonClickCallback()}>
                        <img src={ChevronIcon} className='DocumentPreviewModal-chevron-icon-left' alt='Click to view previous result.'/>
                    </div>
                    
                    {/* Keep-out zone (the actual Document content) */}
                    <div className='DocumentPreviewModal-nav-overlay-keep-out-zone'></div>
                    
                    {/* "Navigate-right" button */}
                    <div className='DocumentPreviewModal-nav-overlay-button' onClick={() => rightNavButtonClickCallback()}>
                        <img src={ChevronIcon} className='DocumentPreviewModal-chevron-icon-right' alt='Click to view next result.'/>
                    </div>

                </div>
            }

            {/* Content: Top-section (contains buttons) */}
            <div className='DocumentPreviewModal-top-section'>
                
                {/* Left content */}
                <div className='DocumentPreviewModal-top-section-left'>
                    {/* Document name */}
                    <div className='DocumentPreviewModal-top-section-document-title'>{documentJson.name}</div>
                </div>
                
                {/* Right content */}
                <div className='DocumentPreviewModal-top-section-right'>

                    {/* Bookmark button */}
                    <div
                        className={`DocumentPreviewModal-top-section-button ${activeEngineBookmarkJson?.some((bookmarkJson) => bookmarkJson.id === uuid) ? 'enabled' : ''}`}
                        onClick={() => handleBookmarkButtonClick()}
                        >
                            {activeEngineBookmarkJson?.some((bookmarkJson) => bookmarkJson.item_id === uuid) && <img className='DocumentPreviewModal-bookmark-icon--filled' src={bookmarkIcon_filled} alt="Click to remove this document from your bookmarks."/>}
                            {!activeEngineBookmarkJson?.some((bookmarkJson) => bookmarkJson.item_id === uuid) && <img className='DocumentPreviewModal-bookmark-icon' src={bookmarkIcon} alt='Click to add this document to your bookmarks.'/>}
                    </div>

                    {/* Close button */}
                    <div className='DocumentPreviewModal-top-section-button'
                        onClick={() => triggerClose()}>
                        <img className='DocumentPreviewModal-close-icon' src={closeIcon} alt='Click to close this document.'/>
                    </div>

                </div>
            </div>

            {/* Bottom content (actual Document content) */}
            <div className='DocumentPreviewModal-bottom-section'>
                
                {/* Title section */}
                <div className='DocumentPreviewModal-bottom-section-title-section'>

                    {/* Document title */}
                    <div className='DocumentPreviewModal-bottom-section-title-section-document-title'>{documentJson?.name}</div>                    

                    {/* Document metadata */}
                    <div className='DocumentPreviewModal-bottom-section-title-section-metadata'>
                        {/* Document Source URL */}
                        <div className='DocumentPreviewModal-bottom-section-title-section-metadata-field'>
                            <a href={documentJson?.redirect_url} target='_blank' className='DocumentPreviewModal-bottom-section-title-section-metadata-content--url'>{documentJson?.redirect_url}</a>
                        </div>
                    </div>
                    
                </div>

                {/* Horizontal line */}
                <div className='DocumentPreviewModal-horizontal-line'></div>

                {/* Document HTML section */}
                <div className='DocumentPreviewModal-HTML-section DocumentPreviewModal-fade-in' dangerouslySetInnerHTML={{ __html: documentJson.body_html }}></div>
            </div>

            {/* Semantic Chunk overlay */}
            { !isReadOnly && 
                <div className='DocumentPreviewModal-semantic-chunk-overlay'>

                    {/* Decrement button */}
                    <img className={`DocumentPreviewModal-semantic-chunk-overlay-decrement-button ${semanticChunkCounter <= 1 ? 'disabled' : ''}`}
                        src={triangleArrow}
                        alt="Click to view the previous chunk."
                        onClick={() => handleSemanticChunkDecrement()}/>

                    {/* Counter */}
                    <div className='DocumentPreviewModal-semantic-chunk-overlay-counter'>{semanticChunkCounter} / {semanticChunkMaximum}</div>

                    {/* Increment button */}
                    <img className={`DocumentPreviewModal-semantic-chunk-overlay-increment-button ${semanticChunkCounter === semanticChunkMaximum ? 'disabled' : ''}`}
                        src={triangleArrow}
                        alt="Click to view the next chunk."
                        onClick={() => handleSemanticChunkIncrement()}/>

                </div>
            }
        </div>
    );
};


// Export statement
export default DocumentPreviewModal;